import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

// Custom error class for Cognito errors
class CognitoAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CognitoAuthError';
  }
}

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
    },
  },
});

export interface CognitoUser {
  userId: string;
  email: string;
  username?: string;
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
  organizationId?: string;
  jobTitle?: string;
  department?: string;
  preferredLanguage?: string;
  locale?: string;
  groups: string[];
  siteAccess: string[];
  venueAccess: string[];
  defaultSiteId?: string;
  supplierId?: string;
}

export type SignInResult = 
  | {
    status: 'success';
    user: CognitoUser;
  }
  | {
    status: 'newPasswordRequired';
    username: string;
  }
  | {
    status: 'mfaRequired';
    challengeName: string;
  }
  | {
    status: 'resetPasswordRequired';
    username: string;
  };

/**
 * Sign in with email/username and password
 * Returns different results based on the sign-in state
 * 
 * If there's already a signed-in user, we sign them out first.
 * This handles stale sessions and allows switching users.
 */
export async function cognitoSignIn(email: string, password: string): Promise<SignInResult> {
  // Check if there's an existing session and sign out first
  // This prevents "There is already a signed in user" errors
  try {
    const existingUser = await getCurrentUser();
    if (existingUser) {
      await amplifySignOut();
    }
  } catch {
    // No existing user, continue with sign in
  }

  const result = await amplifySignIn({
    username: email,
    password,
    options: {authFlowType: 'USER_PASSWORD_AUTH',},
  });

  const { signInStep } = result.nextStep;

  if (signInStep === 'DONE') {
    const user = await getCurrentCognitoUser();
    if (!user) {
      throw new CognitoAuthError('Failed to get user after sign in');
    }
    return {
      status: 'success',
      user,
    };
  }
  
  if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return {
      status: 'newPasswordRequired',
      username: email,
    };
  }
  
  if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' || signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
    return {
      status: 'mfaRequired',
      challengeName: signInStep,
    };
  }
  
  if (signInStep === 'RESET_PASSWORD') {
    return {
      status: 'resetPasswordRequired',
      username: email,
    };
  }
  
  throw new CognitoAuthError(`Unexpected sign-in step: ${signInStep}`);
}

/**
 * Complete the new password challenge (first login)
 */
export async function completeNewPassword(newPassword: string): Promise<CognitoUser> {
  const result = await confirmSignIn({challengeResponse: newPassword,});

  if (result.nextStep.signInStep !== 'DONE') {
    throw new CognitoAuthError(`Unexpected step after password change: ${result.nextStep.signInStep}`);
  }

  const user = await getCurrentCognitoUser();
  if (!user) {
    throw new CognitoAuthError('Failed to get user after password change');
  }
  
  return user;
}

/**
 * Complete MFA challenge
 */
export async function completeMfaChallenge(code: string): Promise<CognitoUser> {
  const result = await confirmSignIn({challengeResponse: code,});

  if (result.nextStep.signInStep !== 'DONE') {
    throw new CognitoAuthError(`Unexpected step after MFA: ${result.nextStep.signInStep}`);
  }

  const user = await getCurrentCognitoUser();
  if (!user) {
    throw new CognitoAuthError('Failed to get user after MFA');
  }
  
  return user;
}

/**
 * Sign out the current user
 */
export async function cognitoSignOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Get the currently authenticated user with all attributes
 */
export async function getCurrentCognitoUser(): Promise<CognitoUser | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const attributes = await fetchUserAttributes();

    const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];

    const parseAccessList = (value: string | undefined): string[] => {
      if (!value) return [];
      return value.split(',').map(s => s.trim()).filter(Boolean);
    };

    return {
      userId: user.userId,
      email: attributes.email ?? '',
      username: user.username,
      givenName: attributes.given_name,
      familyName: attributes.family_name,
      phoneNumber: attributes.phone_number,
      organizationId: attributes['custom:organizationId'],
      jobTitle: attributes['custom:jobTitle'],
      department: attributes['custom:department'],
      preferredLanguage: attributes['custom:preferredLanguage'],
      locale: attributes.locale,
      groups,
      siteAccess: parseAccessList(attributes['custom:siteAccess']),
      venueAccess: parseAccessList(attributes['custom:venueAccess']),
      defaultSiteId: attributes['custom:defaultSiteId'],
      supplierId: attributes['custom:supplierId'],
    };
  } catch {
    return null;
  }
}

/**
 * Get the current access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Get the current ID token (contains user attributes)
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? null;
    
    if (!token) {
      console.warn('[Cognito] No ID token in session. Session tokens:', {
        hasTokens: !!session.tokens,
        hasIdToken: !!session.tokens?.idToken,
        hasAccessToken: !!session.tokens?.accessToken,
      });
    }
    
    return token;
  } catch (error) {
    console.error('[Cognito] Error fetching ID token:', error);
    return null;
  }
}

/**
 * Check if the current session is valid
 */
export async function isSessionValid(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return session.tokens !== undefined;
  } catch {
    return false;
  }
}

/**
 * Request a password reset code
 * Sends an email with a verification code to the user
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Confirm password reset with code and new password
 */
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}
